/**
 * Fait : décrit les documents des collections `fichiersDeProjet` et `prompts`.
 *   Un fichier appartient à un projet — la LOI du critère 7 — et porte les
 *   limites du plan en constantes nommées : formats admis, taille, pages,
 *   stockage par projet. Une consigne, elle, n'a plus de portée : la
 *   bibliothèque est commune, et son auteur seul la réécrit.
 *
 * Ne fait pas : il n'extrait aucun texte — l'extraction est le métier du
 *   worker isolé (phase 6), et les champs qu'elle produira arriveront par
 *   migration — et ne stocke pas le contenu du fichier lui-même, seulement ses
 *   métadonnées.
 */

import { z } from 'zod';

import { identifiant, nomDonne, versionDeRessource } from '../schemas.ts';

/** Formats acceptés — liste blanche du plan (phase 6) : le reste est refusé. */
export const FORMATS_DE_FICHIER = ['pdf', 'txt', 'markdown', 'docx'] as const;

export type FormatDeFichier = (typeof FORMATS_DE_FICHIER)[number];

/** Vie d'un fichier déposé, diffusée en direct aux membres présents (phase 6). */
export const ETATS_DE_FICHIER = ['importe', 'en_traitement', 'refuse', 'pret', 'supprime'] as const;

export type EtatDeFichier = (typeof ETATS_DE_FICHIER)[number];

/** Limites du plan (phase 6) — nommées ici, jamais répétées en chiffres. */
export const TAILLE_MAXIMALE_FICHIER_OCTETS = 10 * 1024 * 1024;
export const PAGES_MAXIMALES_PAR_FICHIER = 50;
export const STOCKAGE_MAXIMAL_PAR_PROJET_OCTETS = 500 * 1024 * 1024;

/**
 * Ce qu'on garde du texte extrait, en caractères.
 *
 * Deux raisons, et la première est une limite dure : un document MongoDB ne
 * peut pas dépasser 16 Mio, or l'extraction peut produire beaucoup plus. La
 * seconde est économique : un mébioctet de texte représente déjà des
 * centaines de milliers de jetons, donc un contexte que personne ne veut
 * payer. Au-delà, le texte est COUPÉ, et le document le dit.
 */
export const TAILLE_MAXIMALE_TEXTE_EXTRAIT = 1024 * 1024;

/** Un document déposé dans un projet. `projetId` requis : c'est la loi. */
export const fichierDeProjet = z
  .object({
    projetId: identifiant,

    /** Nom d'origine, borné : un nom de fichier n'est pas un texte libre. */
    nomFichier: z.string().trim().min(1).max(255),

    format: z.enum(FORMATS_DE_FICHIER),
    tailleOctets: z.number().int().min(1).max(TAILLE_MAXIMALE_FICHIER_OCTETS),
    etat: z.enum(ETATS_DE_FICHIER),
    deposeParId: identifiant,

    /**
     * Le nom des octets SUR LE VOLUME — engendré par le produit, jamais le
     * nom donné par le membre : un nom d'origine peut porter `../` et sortir
     * du dossier. Le nom d'origine ne sert qu'à l'affichage.
     */
    stockageId: identifiant,

    /** Ce que l'extraction a produit — absent tant qu'elle n'a pas eu lieu. */
    texteExtrait: z.string().max(TAILLE_MAXIMALE_TEXTE_EXTRAIT).optional(),
    texteCoupe: z.boolean().optional(),
    pages: z.number().int().nonnegative().optional(),

    /** Pourquoi le fichier est `refuse` — la nature vient du worker. */
    natureDEchec: z.string().max(60).optional(),

    version: versionDeRessource,
    creeLe: z.date(),
    misAJourLe: z.date(),
  })
  .strict();

export type FichierDeProjet = z.infer<typeof fichierDeProjet>;

/**
 * Taille maximale du texte d'un prompt, en caractères. Un prompt est une
 * consigne réutilisable, pas un document : au-delà, c'est un fichier de projet.
 */
export const TAILLE_MAXIMALE_TEXTE_PROMPT = 20_000;

/**
 * Longueur maximale de la DESCRIPTION d'une consigne. Elle sert à la
 * retrouver, pas à la raconter : c'est la phrase qu'on lit dans une liste, et
 * une liste où chaque ligne fait un paragraphe ne se parcourt plus. Le texte,
 * lui, a droit à vingt mille caractères.
 */
export const TAILLE_MAXIMALE_DESCRIPTION_PROMPT = 300;

/**
 * Une consigne réutilisable — une seule forme depuis la migration 012.
 *
 * IL N'Y A PLUS DE PORTÉE. Elle a vécu deux migrations : la 010 a basculé les
 * consignes personnelles en consignes de famille (`D-025`), et la 012 a retiré
 * le champ devenu muet. Toute consigne est lisible par toute la famille ;
 * ÉCRIRE reste le droit de son auteur seul, que `creeParId` nomme.
 *
 * Les prompts sont une bibliothèque transversale : le critère 7 ne les
 * rattache pas à un projet, et c'est voulu — on réutilise un prompt D'UN
 * projet À l'autre.
 */
export const prompt = z
  .object({
    creeParId: identifiant,
    titre: nomDonne,

    /**
     * À quoi sert cette consigne, en une phrase. Elle peut être VIDE : la
     * migration 012 en pose une vide sur les consignes écrites avant qu'elle
     * existe, et refuser ces documents-là les déclarerait illégaux alors
     * qu'ils sont parfaitement légaux.
     */
    description: z.string().max(TAILLE_MAXIMALE_DESCRIPTION_PROMPT),

    texte: z.string().min(1).max(TAILLE_MAXIMALE_TEXTE_PROMPT),
    version: versionDeRessource,
    creeLe: z.date(),
    misAJourLe: z.date(),

    /**
     * LE VESTIGE DE LA 010 (`D-025`, 2026-08-23) : la portée qu'avait cette
     * consigne avant que la bibliothèque devienne commune. Il est déclaré ICI
     * parce qu'il EXISTE en base — un schéma strict qui l'ignorerait
     * déclarerait invalides des documents parfaitement légaux.
     *
     * Il ne sert qu'au démontage de la 010. La migration qui l'effacera est la
     * seule des trois à perdre de l'information : elle mérite une décision,
     * pas un effet de bord, et reste hors du chantier `40.o`.
     */
    porteeAvantLaBascule: z.literal('personnel').optional(),

    /**
     * LE VESTIGE DE LA 012 (2026-08-25), et POURQUOI IL N'EST PAS LE MÊME QUE
     * LE PRÉCÉDENT — le confondre serait le défaut exact que le critère 147
     * appelle « effacer au lieu de déplacer ».
     *
     * Les deux mémorisent une portée, mais pas au même instant, et ils ne
     * coexistent JAMAIS sur un même document :
     *
     *   • `porteeAvantLaBascule` dit l'état d'avant la 010. Une consigne
     *     basculée le porte, et sa portée valait `familial` juste avant la
     *     012 : la lui restituer ici rendrait `personnel` et déferait la 010
     *     par-dessus le marché ;
     *   • `porteeAvantLeRetrait` dit l'état d'avant la 012, et lui seul. Le
     *     portent les consignes ÉCRITES personnelles après la 010 — le
     *     service l'a permis jusqu'à ce lot. Son absence veut dire
     *     `familial`, la portée que tout le reste avait.
     */
    porteeAvantLeRetrait: z.literal('personnel').optional(),
  })
  .strict();

export type Prompt = z.infer<typeof prompt>;
